import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { Search, ExternalLink, FileDown, ListChecks, AlertOctagon, Radar, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ConsultCtaBand from "../components/ConsultCtaBand";
import QuantumScoreDial from "../components/QuantumScoreDial";
import api, { apiClient } from "../lib/api";

import useDocumentTitle from "../hooks/useDocumentTitle";
import useQelliWarmPulse from "../hooks/useQelliWarmPulse";

const PRESETS = [
  { chain: "bitcoin",  label: "Genesis (BTC)",        addr: "1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa" },
  { chain: "ethereum", label: "Vitalik (ETH)",        addr: "0xd8dA6BF26964aF9D7eEd9e03E53415D37aA96045" },
  { chain: "solana",   label: "Wrapped SOL Mint",     addr: "So11111111111111111111111111111111111111112" },
];

export default function ScanPage() {
  const { t } = useTranslation();
  useDocumentTitle('Wallet Exposure Scan — QorTrace');
  const [chains, setChains] = useState([]);
  const [chain, setChain] = useState("bitcoin");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [quotaLock, setQuotaLock] = useState(null);  // {status, code, message, signup_url|upgrade_url}

  useEffect(() => { api.listChains().then(setChains).catch(() => {}); }, []);

  // Iter198 — Qelli warm-pulse for prospects landing on /scan from
  // a shared link. The result-dial is dense; Qelli offers to read
  // it for them.
  useQelliWarmPulse({
    enabled:    true,
    source:     "scan-landing",
    intentText: "Want me to read this exposure result for you?",
    prompt:
      "Explain how the QorTrace scanner classifies wallet exposure " +
      "(Critical / High / Medium / Low / Quasi-resistant) and how " +
      "the 0–100 score is computed — and what the highest-leverage " +
      "next step is to reduce HNDL risk on a critical-tier address.",
  });

  // Phase 4.13.6 — claim badge attribution from URL on first landing.
  // Fires only when utm_source is present so organic /scan visits are no-ops.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utm_source = params.get("utm_source");
    if (!utm_source) return;
    const payload = {
      utm_source,
      utm_medium: params.get("utm_medium") || undefined,
      utm_domain: params.get("utm_domain") || undefined,
      qt_chain:   params.get("qt_chain")   || undefined,
    };
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/attribution/claim`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setQuotaLock(null);
    if (!address.trim()) { setError("Enter a wallet address"); return; }
    setLoading(true);
    setResult(null);
    try {
      const r = await api.scan(chain, address.trim());
      setResult(r);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // 429 → anon over IP rate-limit (signup nudge); 402 → free user
      // over plan-quota (upgrade nudge). Both surface as a structured
      // QuotaLock card instead of a plain inline error.
      if ((status === 429 || status === 402) && detail && typeof detail === "object") {
        setQuotaLock({ status, ...detail });
      } else {
        setError(typeof detail === "string"
          ? detail
          : (detail?.message || err.message || "Scan failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const applyPreset = (p) => { setChain(p.chain); setAddress(p.addr); };

  // Quota pill — fetched once on mount, refreshed after every successful
  // scan + after a quota lock so the surface stays in sync with the BE.
  const [quota, setQuota] = useState(null);
  const refreshQuota = async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scan/quota`,
                            { credentials: "include" });
      if (r.ok) setQuota(await r.json());
    } catch (_) { /* silent — pill is decorative */ }
  };
  useEffect(() => { refreshQuota(); }, []);
  useEffect(() => { if (result || quotaLock) refreshQuota(); }, [result, quotaLock]);

  return (
    <div className="min-h-screen qt-radial-bg">
      <Header />
      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-10">
        <div className="qt-label">QORTRACE SCAN</div>
        <h1 className="qt-h2 text-4xl sm:text-5xl mt-2">{t("scan.title", "Wallet Quantum Exposure")}</h1>
        <p className="mt-3 max-w-2xl" style={{ color: "var(--qt-text-2)" }}>
          {t("scan.subtitle", "Pick a chain, paste an address, and get a deterministic 0–100 exposure score with full factor breakdown and migration playbook.")}
        </p>

        <ScanQuotaPill quota={quota} />

        <form onSubmit={submit} className="mt-8 qt-card" data-testid="scan-form">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-3">
              <div className="qt-label mb-2">CHAIN</div>
              <select
                data-testid="chain-selector"
                className="qt-select w-full"
                value={chain}
                onChange={(e) => setChain(e.target.value)}
              >
                {[...chains].sort((a, b) => a.label.localeCompare(b.label)).map((c) => (
                  <option key={c.id} value={c.id}>{`${c.label} (${c.symbol})`}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-7">
              <div className="qt-label mb-2">WALLET ADDRESS</div>
              <input
                data-testid="address-input"
                className="qt-input"
                placeholder={t("scan.placeholder_address", "Paste a wallet address — BTC, ETH, SOL, TRX, etc.")}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>
            <div className="md:col-span-2 flex items-end">
              <button
                data-testid="scan-submit-button"
                type="submit"
                disabled={loading}
                className="qt-btn qt-btn-primary w-full justify-center"
                style={{ height: 46 }}
              >
                {loading ? t("scan.loading", "SCANNING…").toUpperCase() : (<><Search size={16}/> {t("scan.submit", "SCAN").toUpperCase()}</>)}
              </button>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="qt-label" style={{ alignSelf: "center" }}>TRY:</span>
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                data-testid={`preset-${p.chain}`}
                onClick={() => applyPreset(p)}
                className="qt-btn qt-btn-ghost"
                style={{ fontSize: 12, padding: "6px 10px" }}
              >
                {p.label}
              </button>
            ))}
          </div>
          {error && (
            <div data-testid="scan-error" className="mt-4 px-4 py-3 rounded"
                 style={{ background: "rgba(255,42,42,0.08)", border: "1px solid rgba(255,42,42,0.4)", color: "#FF6E6E" }}>
              <AlertOctagon size={14} className="inline -mt-1 mr-2" />{error}
            </div>
          )}
          {quotaLock && (
            <div
              data-testid="scan-quota-lock"
              className="mt-4 qt-card"
              style={{
                background: "rgba(255,184,0,0.06)",
                border: "1px solid rgba(255,184,0,0.5)",
              }}
            >
              <div className="qt-label" style={{ color: "#FFB800" }}>
                {quotaLock.status === 429 ? "FREE SCAN LIMIT REACHED" : "SCAN QUOTA EXHAUSTED"}
              </div>
              <div className="text-sm mt-2" style={{ color: "#fff", lineHeight: 1.6 }}>
                {quotaLock.message}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {quotaLock.signup_url && (
                  <Link
                    to={quotaLock.signup_url}
                    data-testid="scan-quota-signup"
                    className="qt-btn qt-btn-primary"
                    style={{ fontSize: 13, padding: "0.55rem 1rem" }}
                  >
                    Sign up free <ArrowRight size={12} />
                  </Link>
                )}
                {quotaLock.upgrade_url && (
                  <Link
                    to={quotaLock.upgrade_url}
                    data-testid="scan-quota-upgrade"
                    className={quotaLock.signup_url ? "qt-btn qt-btn-secondary" : "qt-btn qt-btn-primary"}
                    style={{ fontSize: 13, padding: "0.55rem 1rem" }}
                  >
                    See upgrade plans
                  </Link>
                )}
              </div>
            </div>
          )}
        </form>

        {loading && (
          <div className="mt-8 qt-card animate-pulse" style={{ minHeight: 300 }}>
            <div className="qt-label">FETCHING ON-CHAIN DATA · COMPUTING SCORE</div>
          </div>
        )}

        {result && <ScanResult result={result} />}
      </div>
      <ConsultCtaBand source="scan-bottom-cta" variant="card" maxWidth={1400} />
      <Footer />
    </div>
  );
}

function ScanResult({ result }) {
  const data = result.data || {};
  const scoring = result.scoring || {};
  const advisor = result.advisor || {};
  const factors = scoring.factors || [];
  const tier = (scoring.tier || "").toLowerCase();
  const atRisk = tier === "critical" || tier === "high";

  const chartData = useMemo(() => factors.map((f) => ({
    name: f.label,
    score: Number(f.score),
    max: Number(f.weight_max),
  })), [factors]);

  // Custom tooltip with bright readable text on dark bg
  const FactorTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const score = payload.find((p) => p.dataKey === "score")?.value;
    const max = payload.find((p) => p.dataKey === "max")?.value;
    return (
      <div style={{
        background: "rgba(4,9,20,0.96)",
        border: "1px solid var(--qt-cyan)",
        borderRadius: 4,
        padding: "10px 14px",
        fontSize: 12,
        boxShadow: "0 6px 24px rgba(0,229,255,0.18)",
      }}>
        <div style={{ color: "#fff", fontWeight: 700, marginBottom: 6 }}>{label}</div>
        <div className="qt-mono" style={{ color: "#00E5FF" }}>SCORE&nbsp;<span style={{ color: "#fff" }}>{score}</span></div>
        <div className="qt-mono" style={{ color: "#9CAEC9" }}>MAX&nbsp;&nbsp;<span style={{ color: "#fff" }}>{max}</span></div>
      </div>
    );
  };

  return (
    <div className="mt-10" data-testid="scan-result">
      {atRisk && (
        <MonitoringNudge
          chain={data.chain}
          address={data.address}
          tier={scoring.tier}
          score={scoring.score}
        />
      )}

      {/* Top row: dial + key facts */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="qt-card lg:col-span-5 flex flex-col items-center justify-center qt-grain" style={{ padding: 32 }}>
          <QuantumScoreDial score={scoring.score} tier={scoring.tier} size={300} />
          <div className="qt-mono text-xs mt-4" style={{ color: "var(--qt-text-3)" }}>
            METHODOLOGY {result.methodology_version}
          </div>
        </div>
        <div className="lg:col-span-7 grid grid-cols-2 gap-3">
          <KV label="CHAIN"           value={data.chain_label} />
          <KV label="ADDRESS TYPE"    value={data.address_type} />
          <KV label="BALANCE"         value={`${(data.balance_native ?? 0).toLocaleString(undefined, { maximumFractionDigits: 6 })} ${data.symbol}`} />
          <KV label="BALANCE (USD)"   value={`$${(data.balance_usd ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`} accent={data.pubkey_exposed ? "#FF8000" : undefined} />
          <KV label="TX COUNT"        value={(data.tx_count ?? 0).toLocaleString()} />
          <KV label="PUBKEY EXPOSED"  value={data.pubkey_exposed ? "YES" : "NO"} accent={data.pubkey_exposed ? "#FF2A2A" : "#00E5FF"} />
          <KV label="LAST ACTIVITY"   value={data.days_since_last_activity > 9000 ? "—" : `${data.days_since_last_activity} day(s) ago`} />
          <KV label="ADDRESS"         value={data.address?.slice(0, 14) + "…" + data.address?.slice(-6)} mono />
          <div className="col-span-2 flex flex-wrap items-center gap-3 mt-1">
            {data.explorer_url && (
              <a href={data.explorer_url} target="_blank" rel="noreferrer" data-testid="explorer-link"
                 className="qt-btn qt-btn-secondary" style={{ fontSize: 12, padding: "8px 12px" }}>
                Open in explorer <ExternalLink size={14}/>
              </a>
            )}
            <Link
              to={`/report/${result.id}`}
              data-testid="open-report-link"
              className="qt-btn qt-btn-primary"
              style={{ fontSize: 12, padding: "8px 12px" }}
            >
              <FileDown size={14}/> Open report (PDF-ready)
            </Link>
          </div>
        </div>
      </div>

      {/* Factor chart */}
      <div className="qt-card mt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="qt-label">FACTOR BREAKDOWN</div>
            <div className="qt-h2 text-xl mt-1">How the score is constructed</div>
          </div>
        </div>
        <div style={{ width: "100%", height: 320, minWidth: 0, minHeight: 0 }}>
          <ResponsiveContainer minWidth={0} minHeight={0} width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 30, top: 10, bottom: 10 }}>
              <XAxis type="number" stroke="#5D7394" fontSize={11} domain={[0, "dataMax"]} />
              <YAxis dataKey="name" type="category" stroke="#9CAEC9" fontSize={11} width={220}
                     tick={{ fill: "#9CAEC9" }} interval={0} />
              <Tooltip content={<FactorTooltip />} cursor={{ fill: "rgba(0,229,255,0.06)" }} />
              <Bar dataKey="max"   fill="#162440" radius={[0, 2, 2, 0]} />
              <Bar dataKey="score" radius={[0, 2, 2, 0]}>
                {chartData.map((_, i) => <Cell key={i} fill="#00E5FF" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {factors.map((f) => (
            <div key={f.key} className="qt-card" style={{ padding: 14, background: "rgba(4,9,20,0.5)" }}>
              <div className="flex items-baseline justify-between gap-3">
                <div className="text-sm font-semibold">{f.label}</div>
                <div className="qt-mono text-xs" style={{ color: "var(--qt-text-3)" }}>{f.score} / {f.weight_max}</div>
              </div>
              <div className="text-xs mt-1.5" style={{ color: "var(--qt-text-2)", lineHeight: 1.5 }}>{f.evidence}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Advisor narrative */}
      {advisor && (advisor.executive_summary || advisor.migration_playbook) && (
        <div className="qt-card mt-6">
          <div className="qt-label">ADVISOR · GROUNDED NARRATIVE</div>
          {advisor.executive_summary && (
            <p className="mt-3 text-base" style={{ color: "#fff", lineHeight: 1.6 }}>{advisor.executive_summary}</p>
          )}
          {advisor.key_risks?.length > 0 && (
            <ul className="mt-4 space-y-2">
              {advisor.key_risks.map((r, i) => (
                <li key={i} className="flex gap-2 text-sm" style={{ color: "var(--qt-text-2)" }}>
                  <span style={{ color: "var(--qt-amber)" }}>◆</span><span>{r}</span>
                </li>
              ))}
            </ul>
          )}
          {advisor.migration_playbook?.length > 0 && (
            <div className="mt-6">
              <div className="qt-label flex items-center gap-2 mb-3">
                <ListChecks size={12} /> MIGRATION PLAYBOOK
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {advisor.migration_playbook.map((s) => (
                  <div key={s.step} className="qt-card" style={{ padding: 14, background: "rgba(4,9,20,0.5)" }}>
                    <div className="qt-mono text-xs" style={{ color: "var(--qt-cyan)" }}>STEP {s.step}</div>
                    <div className="mt-1 font-semibold text-sm">{s.title}</div>
                    <div className="text-xs mt-1.5" style={{ color: "var(--qt-text-2)", lineHeight: 1.5 }}>{s.detail}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KV({ label, value, accent, mono }) {
  return (
    <div className="qt-card" style={{ padding: 14, background: "rgba(4,9,20,0.5)" }}>
      <div className="qt-label">{label}</div>
      <div
        className={mono ? "qt-mono" : ""}
        style={{
          fontSize: 18,
          fontWeight: 600,
          marginTop: 4,
          color: accent || "#fff",
          letterSpacing: mono ? "-0.02em" : "normal",
          fontFamily: mono ? "JetBrains Mono, monospace" : "inherit",
        }}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function MonitoringNudge({ chain, address, tier, score }) {
  const isCritical = (tier || "").toLowerCase() === "critical";
  // Default copy (used until /api/nudge/scan-critical responds — keeps the
  // UI from flashing). API will swap these in based on assigned variant.
  const fallback = isCritical
    ? {
        label:    "CRITICAL RISK · ACT NOW",
        headline: "Keep watching {address} — add it to monitoring and we'll email you the moment the score shifts.",
        cta:      "Monitor this address",
      }
    : {
        label:    "HIGH RISK · STAY AHEAD",
        headline: "Keep watching {address} — add it to monitoring and we'll email you the moment the score shifts.",
        cta:      "Monitor this address",
      };
  const [variant, setVariant] = useState("v1");
  const [copy, setCopy] = useState(fallback);
  const [viewLogged, setViewLogged] = useState(false);

  useEffect(() => {
    let alive = true;
    apiClient
      .get(`/nudge/scan-critical?tier=${isCritical ? "critical" : "high"}`,
           { withCredentials: true })
      .then((r) => {
        if (!alive) return;
        const v = r?.data?.variant || "v1";
        const c = r?.data?.copy || fallback;
        setVariant(v);
        setCopy({
          label:    c.label || fallback.label,
          headline: c.headline || fallback.headline,
          cta:      c.cta || fallback.cta,
        });
      })
      .catch(() => { /* silently fall back to control copy */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCritical]);

  // Log a view event once per (variant, address) pair.
  useEffect(() => {
    if (viewLogged || !address) return;
    apiClient
      .post("/nudge/scan-critical/event", {
        variant,
        event_type: "view",
        tier:       (tier || "").toLowerCase(),
        score,
        chain,
        address,
        source:     "scan-result",
      }, { withCredentials: true })
      .catch(() => { /* analytics best-effort */ });
    setViewLogged(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, address]);

  const onClick = () => {
    apiClient
      .post("/nudge/scan-critical/event", {
        variant,
        event_type: "click",
        tier:       (tier || "").toLowerCase(),
        score,
        chain,
        address,
        source:     "scan-result",
      }, { withCredentials: true })
      .catch(() => { /* nav still proceeds */ });
  };

  const href = `/account/monitoring?chain=${encodeURIComponent(chain || "")}&address=${encodeURIComponent(address || "")}&source=scan-nudge&v=${encodeURIComponent(variant)}`;
  const shortAddr =
    address && address.length > 20 ? `${address.slice(0, 10)}…${address.slice(-6)}` : address;
  const color = isCritical ? "#FF6E6E" : "#FF9500";
  const bg = isCritical ? "rgba(255,42,42,0.08)" : "rgba(255,149,0,0.08)";
  const borderBase = isCritical ? "rgba(255,110,110,0.45)" : "rgba(255,149,0,0.5)";
  return (
    <Link
      to={href}
      onClick={onClick}
      data-testid="scan-monitoring-nudge"
      data-tier={(tier || "").toLowerCase()}
      data-variant={variant}
      className="qt-monitoring-nudge flex items-center flex-wrap gap-3 px-5 py-4 mb-6 rounded"
      style={{
        background: bg,
        border: `1px solid ${borderBase}`,
        textDecoration: "none",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <span
        aria-hidden
        className="qt-pulse"
        style={{
          width: 10, height: 10, borderRadius: 99,
          background: color, flexShrink: 0,
          boxShadow: `0 0 12px ${color}`,
        }}
      />
      <div className="flex-1 min-w-[220px]">
        <div
          className="qt-mono text-[10px]"
          data-testid="scan-nudge-label"
          style={{ color, letterSpacing: "0.18em", fontWeight: 700 }}
        >
          {copy.label} · SCORE {score}
        </div>
        <div className="text-sm sm:text-base mt-0.5"
             data-testid="scan-nudge-headline"
             style={{ color: "#fff", fontWeight: 500 }}>
          {/*
            Swap the {address} placeholder for the cyan-styled short
            address inline. Falls back to "this address" if the variant
            copy somehow doesn't include the placeholder (defensive).
          */}
          {(() => {
            const headline = copy.headline || "";
            const placeholder = "{address}";
            const idx = headline.indexOf(placeholder);
            if (idx === -1 || !shortAddr) {
              return headline.replace(placeholder, "this address");
            }
            return (
              <>
                {headline.slice(0, idx)}
                <span className="qt-mono text-xs" style={{ color: "var(--qt-cyan)" }}>
                  {shortAddr}
                </span>
                {headline.slice(idx + placeholder.length)}
              </>
            );
          })()}
        </div>
      </div>
      <span
        data-testid="scan-nudge-cta"
        className="qt-btn qt-btn-primary"
        style={{ fontSize: 12, padding: "8px 14px", whiteSpace: "nowrap" }}
      >
        <Radar size={14} /> {copy.cta} <ArrowRight size={14} />
      </span>
    </Link>
  );
}


// Inline-rendered usage pill near the scan form.
//   anon → "X anon scans left today · sign up for 50 more"
//   free → "X / 50 lifetime scans · upgrade for monthly buckets"
//   pro/team → "X / 1000 scans this month"
//   unlimited → no pill (decorative-only)
function ScanQuotaPill({ quota }) {
  if (!quota) return null;
  if (quota.scope === "unlimited") return null;
  let copy = "";
  let cta = null;
  let accent = "var(--qt-cyan)";
  const remaining = quota.remaining ?? 0;
  if (quota.plan === "anon") {
    copy = `${remaining} free scan${remaining === 1 ? "" : "s"} left — sign up for unlimited`;
    cta = { to: "/signin?next=/scan", label: "Sign up free" };
    if (remaining === 0) accent = "#FF6E6E";
    else if (remaining <= 2) accent = "#FFB000";
  } else if (quota.plan === "free") {
    copy = `${remaining} of ${quota.limit} lifetime scans remaining`;
    cta = { to: "/pricing", label: "Upgrade for monthly buckets" };
    if (remaining === 0) accent = "#FF6E6E";
    else if (remaining <= 5) accent = "#FFB000";
  } else if (quota.plan === "pro" || quota.plan === "team") {
    copy = `${quota.used} of ${quota.limit} scans used this month`;
    if (remaining === 0) { accent = "#FF6E6E"; cta = { to: "/pricing", label: "Upgrade tier" }; }
    else if (remaining <= Math.max(5, Math.floor(quota.limit * 0.1))) {
      accent = "#FFB000"; cta = { to: "/pricing", label: "Upgrade tier" };
    }
  } else { return null; }
  return (
    <div
      data-testid="scan-quota-pill"
      data-plan={quota.plan}
      data-remaining={remaining}
      className="mt-5 inline-flex items-center gap-3 qt-mono text-xs px-3 py-1.5 rounded-full"
      style={{
        background: "rgba(255,255,255,0.03)",
        color: accent,
        border: `1px solid ${accent}`,
        letterSpacing: "0.06em",
        fontWeight: 600,
      }}
    >
      <span className="qt-pulse" style={{ width: 6, height: 6, borderRadius: 99, background: accent }} />
      <span style={{ textTransform: "uppercase" }}>{copy}</span>
      {cta && (
        <Link to={cta.to}
              data-testid="scan-quota-pill-cta"
              style={{ color: accent, fontWeight: 700, textDecoration: "underline", textDecorationStyle: "dotted" }}>
          {cta.label} →
        </Link>
      )}
    </div>
  );
}
